/**
 * 공통 페이지 헤더 컴포넌트
 * 중복된 PageHeader, PageTitle, PageDescription 스타일을 통합
 */

import React from 'react';
import styled from 'styled-components';
import { colors, typography, spacing } from '../../styles/designTokens';

// ===== 타입 정의 =====
export interface PageHeaderProps {
    title: string;
    description?: string;
    children?: React.ReactNode;
    /** 모바일 전용 뷰 등에서 타이틀 스케일만 축소 */
    variant?: 'default' | 'compact';
}

// ===== 스타일드 컴포넌트 =====
const PageHeaderContainer = styled.div<{ $compact?: boolean }>`
  margin-bottom: ${({ $compact }) => ($compact ? spacing[4] : spacing[6])};
`;

const PageTitle = styled.h1<{ $compact?: boolean }>`
  font-size: ${({ $compact }) =>
    $compact ? typography.fontSize['2xl'] : typography.fontSize['3xl']};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.semantic.text.primary};
  margin: 0 0 ${spacing[2]} 0;
  line-height: ${typography.lineHeight.tight};
`;

const PageDescription = styled.p<{ $compact?: boolean }>`
  font-size: ${({ $compact }) =>
    $compact ? typography.fontSize.sm : typography.fontSize.base};
  color: ${colors.semantic.text.secondary};
  margin: 0;
  line-height: ${typography.lineHeight.relaxed};
`;

const PageHeaderContent = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[4]};
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: ${spacing[3]};
  }
`;

const PageHeaderText = styled.div`
  flex: 1;
`;

const PageHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  
  @media (max-width: 768px) {
    justify-content: flex-start;
  }
`;

// ===== 메인 컴포넌트 =====
export const PageHeader: React.FC<PageHeaderProps> = ({
    title,
    description,
    children,
    variant = 'default',
}) => {
    const compact = variant === 'compact';
    return (
        <PageHeaderContainer $compact={compact}>
            <PageHeaderContent>
                <PageHeaderText>
                    <PageTitle $compact={compact}>{title}</PageTitle>
                    {description && (
                        <PageDescription $compact={compact}>{description}</PageDescription>
                    )}
                </PageHeaderText>
                {children && (
                    <PageHeaderActions>
                        {children}
                    </PageHeaderActions>
                )}
            </PageHeaderContent>
        </PageHeaderContainer>
    );
};

export default PageHeader;
